import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useProject } from '../hooks/useProjects';
const LAST_PROJECT_KEY = 'last_project_id';
const ProjectContext = /*#__PURE__*/ createContext(undefined);
/**
 * Provider component that wraps project-scoped routes.
 * Derives project state from URL params and fetches project data.
 * Stores last selected project in localStorage.
 */ export function ProjectProvider({ children }) {
    const params = useParams();
    // Support both /project/:projectId and /proposal/:id routes
    const projectId = params.projectId ?? params.id ?? null;
    const isProjectMode = !!projectId;
    // Fetch project data when in project mode
    const { data, isLoading, error } = useProject(projectId);
    // Store last project ID in localStorage for quick access
    useEffect(()=>{
        if (projectId) {
            localStorage.setItem(LAST_PROJECT_KEY, projectId);
        }
    }, [
        projectId
    ]);
    const value = useMemo(()=>{
        const project = data ? {
            ...data.project,
            members: data.members,
            role: data.role,
            memberCount: data.memberCount
        } : null;
        const role = data?.role ?? null;
        const canEdit = role === 'owner' || role === 'member';
        const isOwner = role === 'owner';
        const isArchived = data?.project.status === 'archived';
        return {
            projectId,
            isProjectMode,
            project,
            members: data?.members ?? [],
            role,
            isLoading,
            error: error,
            canEdit,
            isOwner,
            isArchived
        };
    }, [
        projectId,
        isProjectMode,
        data,
        isLoading,
        error
    ]);
    return /*#__PURE__*/ _jsx(ProjectContext.Provider, {
        value: value,
        children: children
    });
}
/**
 * Hook to access project context.
 * Must be used within a ProjectProvider.
 */ export function useProjectContext() {
    const context = useContext(ProjectContext);
    if (context === undefined) {
        throw new Error('useProjectContext must be used within a ProjectProvider');
    }
    return context;
}
/**
 * Get the last selected project ID from localStorage.
 * Useful for redirecting to last viewed project.
 */ export function getLastProjectId() {
    return localStorage.getItem(LAST_PROJECT_KEY);
}
/**
 * Clear the last selected project ID from localStorage.
 */ export function clearLastProjectId() {
    localStorage.removeItem(LAST_PROJECT_KEY);
}
